import React from "react";
import Loader from "../../component/common/Loader";
import { mergeManagerData } from "../../component/common/aparManagerUtils";
import { formatDateDDMMYY } from "../../utils";
import {
  getKpiReport,
  getSelfAppraisal,
  getReportingAssessment,
  getKpiTaskGrade,
  getPersonalAttributeGrade,
  getPersonalAttributeMasterList,
  getReviewingAssessment,
} from "../../actions/AppraisalCycleAction";

const INTEGRITY_LABELS: any = {
  beyond_doubt: "Beyond doubt",
  doubtful: "Integrity is doubtful (secret note attached)",
  nothing_adverse: "Nothing adverse received",
};

export const APAR_DETAIL_SECTIONS = [
  { id: "apar-detail-workplan", label: "Work Plan", roles: ["reporting", "reviewing", "accepting"] },
  { id: "apar-detail-self", label: "Self Appraisal", roles: ["reporting", "reviewing", "accepting"] },
  { id: "apar-detail-reporting", label: "Reporting Manager", roles: ["reviewing", "accepting"] },
  { id: "apar-detail-reviewing", label: "Reviewing Manager", roles: ["accepting"] },
];

/**
 * Read-only view of everything filled for an employee's APAR up to the viewer's stage.
 * Shared by the "View Details" page and the modal on the manager's final step.
 * Props: viewerRole, employeeData ({ employeeCode, effectiveFrom, ... }), kpiReportId, onEmployeeDataLoaded
 */
class AparFullDetailContent extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      showLoader: true,
      employeeData: props.employeeData || {},
      kpiReport: null,
      selfAppraisal: null,
      reportingAssessment: null,
      repKpiGrades: [],
      repAttrGrades: [],
      attrMasterList: [],
      reviewingAssessment: null,
      revKpiGrades: [],
      revAttrGrades: [],
    };
  }

  componentDidMount() {
    const { employeeData, viewerRole, kpiReportId } = this.props;
    this.loadAllData(employeeData || {}, viewerRole, kpiReportId);
  }

  loadAllData = async (employeeData: any, viewerRole: string, passedKpiReportId: any) => {
    const { employeeCode, effectiveFrom } = employeeData;
    try {
      const kpiRes = await getKpiReport({ employeeCode, effectiveFrom, reportType: "employee" });

      const kpiReport = kpiRes?.result || kpiRes || null;
      const kpiReportId = kpiReport?.id || passedKpiReportId || null;

      this.setState((prev: any) => ({
        kpiReport,
        employeeData: { ...prev.employeeData, ...mergeManagerData(prev.employeeData, kpiReport || {}) },
      }));

      if (!kpiReportId) {
        this.setState({ showLoader: false }, () => this.props.onEmployeeDataLoaded && this.props.onEmployeeDataLoaded(this.state.employeeData));
        return;
      }

      const employeeFetches = [
        getSelfAppraisal({ kpiReportId }),
      ];

      const reportingFetches: Promise<any>[] =
        viewerRole === "reviewing" || viewerRole === "accepting"
          ? [
              getReportingAssessment({ kpiReportId }),
              getKpiTaskGrade({ kpiReportId, gradedByRole: "reporting" }),
              getPersonalAttributeGrade({ kpiReportId, gradedByRole: "reporting" }),
              getPersonalAttributeMasterList(),
            ]
          : [];

      const reviewingFetches: Promise<any>[] =
        viewerRole === "accepting"
          ? [
              getReviewingAssessment({ kpiReportId }),
              getKpiTaskGrade({ kpiReportId, gradedByRole: "reviewing" }),
              getPersonalAttributeGrade({ kpiReportId, gradedByRole: "reviewing" }),
            ]
          : [];

      const [empResults, repResults, revResults] = await Promise.all([
        Promise.all(employeeFetches),
        Promise.all(reportingFetches),
        Promise.all(reviewingFetches),
      ]);

      const [selfAppraisalRes] = empResults;

      const selfAppraisal = selfAppraisalRes?.result || null;

      const newState: any = { selfAppraisal };

      if (viewerRole === "reviewing" || viewerRole === "accepting") {
        const [repAssessRes, repKpiGradeRes, repAttrGradeRes, attrMasterRes] = repResults;
        const reportingAssessment = repAssessRes?.result || repAssessRes || null;
        const repKpiRaw = repKpiGradeRes?.result || repKpiGradeRes;
        const repKpiGrades = repKpiRaw?.grades || (Array.isArray(repKpiRaw) ? repKpiRaw : []);
        const repAttrRaw = repAttrGradeRes?.result || repAttrGradeRes;
        const repAttrGrades = repAttrRaw?.grades || (Array.isArray(repAttrRaw) ? repAttrRaw : []);
        const masterRaw = attrMasterRes?.result || attrMasterRes || [];
        const attrMasterList = [...masterRaw].sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
        Object.assign(newState, { reportingAssessment, repKpiGrades, repAttrGrades, attrMasterList });
      }

      if (viewerRole === "accepting") {
        const [revAssessRes, revKpiGradeRes, revAttrGradeRes] = revResults;
        const reviewingAssessment = revAssessRes?.result || revAssessRes || null;
        const revKpiRaw = revKpiGradeRes?.result || revKpiGradeRes;
        const revKpiGrades = revKpiRaw?.grades || (Array.isArray(revKpiRaw) ? revKpiRaw : []);
        const revAttrRaw = revAttrGradeRes?.result || revAttrGradeRes;
        const revAttrGrades = revAttrRaw?.grades || (Array.isArray(revAttrRaw) ? revAttrRaw : []);
        Object.assign(newState, { reviewingAssessment, revKpiGrades, revAttrGrades });
      }

      this.setState((prev: any) => {
        let mergedEmployeeData = { ...prev.employeeData };
        if (newState.reportingAssessment) {
          mergedEmployeeData = { ...mergedEmployeeData, ...mergeManagerData(mergedEmployeeData, newState.reportingAssessment) };
        }
        if (newState.reviewingAssessment) {
          mergedEmployeeData = { ...mergedEmployeeData, ...mergeManagerData(mergedEmployeeData, newState.reviewingAssessment) };
        }
        return { ...newState, showLoader: false, employeeData: mergedEmployeeData };
      }, () => this.props.onEmployeeDataLoaded && this.props.onEmployeeDataLoaded(this.state.employeeData));
    } catch {
      this.setState({ showLoader: false });
    }
  };

  // ─── Section renderers ────────────────────────────────────────────────────

  renderSectionHeader = (id: string, icon: string, title: string, subtitle?: string) => (
    <div className="row mb-3" id={id} style={{ scrollMarginTop: 60 }}>
      <div className="col-md-12">
        <h2 className="fs-5 fw-bold border-bottom pb-2 mb-1">
          <i className={`fa ${icon} mr-2 text-primary`} />
          {title}
        </h2>
        {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
      </div>
    </div>
  );

  renderReadOnlyField = (label: string, value: string, rows?: number) => (
    <div className="form-group mb-3">
      <label className="font-weight-bold small text-muted">{label}</label>
      {rows ? (
        <textarea className="form-control form-control-sm bg-light text-dark" rows={rows} value={value || "—"} readOnly style={{color: '#212529'}} />
      ) : (
        <input type="text" className="form-control form-control-sm bg-light text-dark" value={value || "—"} readOnly style={{color: '#212529'}} />
      )}
    </div>
  );

  renderKpiReportSection = () => {
    const { kpiReport } = this.state;
    if (!kpiReport) return <p className="text-muted fst-italic">No KPI report found.</p>;

    const mouTask = kpiReport.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport.tasks?.filter((t: any) => t.particular !== "*)") || [];

    return (
      <>
        {this.renderSectionHeader("apar-detail-workplan", "fa-list-alt", "Part I — Annual Work Plan & Achievements")}
        <div className="table-responsive mb-4">
          <table className="table table-bordered table-sm align-middle">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "25%" }}>Task Description</th>
                <th style={{ width: "8%" }}>Weightage</th>
                <th style={{ width: "25%" }}>Deliverables / Targets</th>
                <th style={{ width: "37%" }}>Achievement</th>
              </tr>
            </thead>
            <tbody>
              {mouTask && (
                <>
                  <tr className="table-secondary">
                    <td colSpan={5} className="fw-semibold small">I – MoU Target</td>
                  </tr>
                  <tr>
                    <td className="text-muted">*)</td>
                    <td>{mouTask.task_description || "—"}</td>
                    <td className="text-center">{mouTask.weightage || 0}%</td>
                    <td>{mouTask.deliverables || "—"}</td>
                    <td>{mouTask.achievement || <span className="text-muted fst-italic">Not filled</span>}</td>
                  </tr>
                </>
              )}
              <tr className="table-secondary">
                <td colSpan={5} className="fw-semibold small">II – Other Key Assigned Tasks</td>
              </tr>
              {otherTasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-muted fst-italic text-center">No tasks added</td>
                </tr>
              ) : (
                otherTasks.map((task: any) => (
                  <tr key={task.id}>
                    <td className="text-muted">{task.particular}</td>
                    <td>{task.task_description || "—"}</td>
                    <td className="text-center">{task.weightage || 0}%</td>
                    <td>{task.deliverables || "—"}</td>
                    <td>{task.achievement || <span className="text-muted fst-italic">Not filled</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  renderBadge = (value: any) => {
    if (value === null || value === undefined || value === '') return <span className="text-muted">—</span>;
    const isYes = value === true || String(value).toLowerCase() === "yes";
    return (
      <span className={`badge ${isYes ? "badge-success" : "badge-secondary"}`}>
        {isYes ? "Yes" : "No"}
      </span>
    );
  };

  renderSelfAppraisalSection = () => {
    const { selfAppraisal } = this.state;
    return (
      <>
        {this.renderSectionHeader("apar-detail-self", "fa-pencil-square-o", "Part II — Self Appraisal (Employee's Narrative)")}
        {!selfAppraisal ? (
          <p className="text-muted fst-italic mb-4">Employee has not submitted self appraisal details.</p>
        ) : (
          <>
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label className="font-weight-bold small">1. Brief description of responsibilities:</label>
                  <p className="text-muted small mb-1">(Objectives of the position you hold and the responsibilities you are required to discharge, in about 100 words)</p>
                  <textarea className="form-control form-control-sm bg-light text-dark" rows={4} value={selfAppraisal.brief_responsibilities || "—"} readOnly style={{color: '#212529'}} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="font-weight-bold small">2. Exceptional Contribution:</label>
                  <p className="text-muted small mb-1">During the period under report, do you believe that you have made any exceptional contribution, e.g. successful completion of an extraordinarily challenging task or major systemic improvement (resulting in significant benefits to the Company and/or reduction in time and costs)? If so, please give a verbal description (within 100 words):</p>
                  <textarea className="form-control form-control-sm bg-light text-dark" rows={4} value={selfAppraisal.exceptional_contribution || "—"} readOnly style={{color: '#212529'}} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="font-weight-bold small">3. Constraints that Hindered Performance:</label>
                  <p className="text-muted small mb-1">What are the constraints that hindered your performance?</p>
                  <textarea className="form-control form-control-sm bg-light text-dark" rows={4} value={selfAppraisal.constraints_hindered || "—"} readOnly style={{color: '#212529'}} />
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-1">
                  <label className="font-weight-bold small">4. Please indicate specific areas of training that will add value to you:</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="text-muted small mb-1">For the current assignment:</label>
                  <textarea className="form-control form-control-sm bg-light text-dark" rows={3} value={selfAppraisal.training_current_assignment || "—"} readOnly style={{color: '#212529'}} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="text-muted small mb-1">For your future career:</label>
                  <textarea className="form-control form-control-sm bg-light text-dark" rows={3} value={selfAppraisal.training_future_career || "—"} readOnly style={{color: '#212529'}} />
                </div>
              </div>
            </div>

            {/* Declaration */}
            <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">Declaration</h6>
            <div className="table-responsive mb-4">
              <table className="table table-bordered align-middle">
                <tbody>
                  <tr>
                    <td style={{ width: "60%" }}>
                      Have you filed your immovable property return in the prescribed format as due?
                    </td>
                    <td style={{ width: "15%" }} className="text-center">
                      {this.renderBadge(selfAppraisal.immovable_property_filed ?? selfAppraisal.immovablePropertyFiled)}
                    </td>
                    <td style={{ width: "25%" }}>
                      {(selfAppraisal.immovable_property_filed === true || selfAppraisal.immovable_property_filed === 1 || selfAppraisal.immovable_property_filed === "yes" || selfAppraisal.immovablePropertyFiled === "yes") && (
                        <span className="small text-muted">
                          Date: <strong>{(() => {
                            const raw = selfAppraisal.immovable_property_date || selfAppraisal.immovablePropertyDate;
                            if (!raw) return "—";
                            return formatDateDDMMYY(raw) || raw;
                          })()}</strong>
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Have you undergone the suggested medical check up?</td>
                    <td className="text-center">
                      {this.renderBadge(selfAppraisal.medical_check_up ?? selfAppraisal.medicalCheckUp)}
                    </td>
                    <td>
                      {(selfAppraisal.medical_check_up === true || selfAppraisal.medical_check_up === "yes" || selfAppraisal.medicalCheckUp === "yes") && (
                        <>
                          {selfAppraisal.medical_report_file ? (() => {
                            const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/v1\/?$/, '');
                            const fileUrl = `${baseUrl}/medical-report/${selfAppraisal.kpi_report_id}/${selfAppraisal.medical_report_file}`;
                            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(selfAppraisal.medical_report_file);
                            return (
                              <div>
                                {isImage && (
                                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={fileUrl}
                                      alt="Medical Report"
                                      style={{ maxWidth: 120, maxHeight: 80, objectFit: 'cover', border: '1px solid #dee2e6', borderRadius: 4, display: 'block', marginBottom: 4 }}
                                    />
                                  </a>
                                )}
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="small">
                                  <i className="fa fa-file mr-1" />
                                  {selfAppraisal.medical_report_original_name || selfAppraisal.medical_report_file}
                                </a>
                              </div>
                            );
                          })() : (
                            <span className="small text-muted">No file uploaded</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Have you set the annual work plan for all officers for the current year,
                      in respect of whom you are the Reporting Authority?
                    </td>
                    <td className="text-center">
                      {this.renderBadge(selfAppraisal.annual_work_plan_set ?? selfAppraisal.annualWorkPlanSet)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  };


  renderReportingAssessmentSection = () => {
    const { kpiReport, reportingAssessment, repKpiGrades, repAttrGrades, attrMasterList } = this.state;

    const gradeMap: any = {};
    repKpiGrades.forEach((g: any) => { gradeMap[g.kpi_task_id] = g; });

    const attrGradeMap: any = {};
    repAttrGrades.forEach((g: any) => { attrGradeMap[g.attribute_master_id] = g; });

    const mouTask = kpiReport?.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport?.tasks?.filter((t: any) => t.particular !== "*)") || [];

    const mouWeighted = mouTask && gradeMap[mouTask.id] ? parseFloat(gradeMap[mouTask.id].weighted_grade) || 0 : 0;
    const otherWeightedTotal = otherTasks.reduce((sum: number, t: any) => {
      const g = gradeMap[t.id];
      return sum + (g ? parseFloat(g.weighted_grade) || 0 : 0);
    }, 0);
    const grandTotal = mouWeighted + otherWeightedTotal;

    return (
      <>
        <hr className="my-4" />
        {this.renderSectionHeader("apar-detail-reporting", "fa-user-circle", "Reporting Manager's Assessment", "Assessment submitted by the Reporting Authority")}

        {!reportingAssessment ? (
          <p className="text-muted fst-italic mb-4">Reporting Manager has not submitted their assessment yet.</p>
        ) : (
          <>
            {/* Work Plan Assessment */}
            <h6 className="fw-bold mb-3 text-secondary">A. Assessment of Work Plan</h6>
            <div className="row mb-4">
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "1. Please state whether you agree with the responses relating to the accomplishments of the work plan as filled out in Section II. If not, please furnish factual details.",
                  reportingAssessment.agree_accomplishments || "",
                  3
                )}
              </div>
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "2. Please comment on the claim (if any) made by the officer reported upon about his exceptional contribution.",
                  reportingAssessment.exceptional_contribution_comment || "",
                  3
                )}
              </div>
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "3. Has the officer reported upon met with any significant shortfall in achieving the targets? If yes, please furnish factual details.",
                  reportingAssessment.significant_shortfall || "",
                  3
                )}
              </div>
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "4. Do you agree with the constraints mentioned by the officer reported upon that had hindered his performance and, if so, to what extent?",
                  reportingAssessment.agree_constraints || "",
                  3
                )}
              </div>
              <div className="col-md-12">
                {this.renderReadOnlyField(
                  "5. Do you agree with the competency up-gradation needs as identified by the officer?",
                  reportingAssessment.agree_competency_needs || "",
                  2
                )}
              </div>
            </div>

            {/* KPI Task Grades */}
            <h6 className="fw-bold mb-3 text-secondary">B. Assessment of Achievements (KPI Grades)</h6>
            <div className="table-responsive mb-4">
              <table className="table table-bordered table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Task Description</th>
                    <th className="text-center">Weightage (a)</th>
                    <th className="text-center">Absolute Grade (b)</th>
                    <th className="text-center">Weighted Grade (a×b/10)</th>
                  </tr>
                </thead>
                <tbody>
                  {mouTask && (
                    <>
                      <tr className="table-secondary">
                        <td colSpan={5} className="fw-semibold small">I – MoU Target</td>
                      </tr>
                      <tr>
                        <td className="text-muted">*)</td>
                        <td>{mouTask.task_description || "—"}</td>
                        <td className="text-center">{mouTask.weightage || 0}</td>
                        <td className="text-center">{gradeMap[mouTask.id] ? gradeMap[mouTask.id].absolute_grade : "—"}</td>
                        <td className="text-center">{mouWeighted.toFixed(2)}</td>
                      </tr>
                    </>
                  )}
                  <tr className="table-secondary">
                    <td colSpan={5} className="fw-semibold small">II – Other Key Tasks</td>
                  </tr>
                  {otherTasks.map((task: any) => {
                    const g = gradeMap[task.id];
                    const wg = g ? parseFloat(g.weighted_grade) || 0 : 0;
                    return (
                      <tr key={task.id}>
                        <td className="text-muted">{task.particular}</td>
                        <td>{task.task_description || "—"}</td>
                        <td className="text-center">{task.weightage || 0}</td>
                        <td className="text-center">{g ? g.absolute_grade : "—"}</td>
                        <td className="text-center">{wg.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-secondary fw-bold">
                    <td colSpan={4} className="text-end pe-3">Grand Total</td>
                    <td className="text-center">{grandTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Personal Attributes */}
            <h6 className="fw-bold mb-3 text-secondary">C. Personal Attributes Grading</h6>
            {attrMasterList.length === 0 ? (
              <p className="text-muted fst-italic small mb-4">No attributes data.</p>
            ) : (
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Attribute</th>
                      <th className="text-center">Grade (0–10)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attrMasterList.map((attr: any, i: number) => {
                      const g = attrGradeMap[attr.id];
                      return (
                        <tr key={attr.id}>
                          <td>{i + 1}</td>
                          <td>{attr.attribute_name}</td>
                          <td className="text-center">{g ? g.grade : "—"}</td>
                        </tr>
                      );
                    })}
                    {reportingAssessment.attr_total && (
                      <tr className="table-secondary fw-bold">
                        <td colSpan={2} className="text-end pe-3">Attributes Total</td>
                        <td className="text-center">{parseFloat(reportingAssessment.attr_total).toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Integrity, Pen Picture, Overall Grade */}
            <h6 className="fw-bold mb-3 text-secondary">D. Integrity, Pen Picture & Overall Grade</h6>
            <div className="row mb-4">
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "8. Integrity (Please comment on the integrity of the officer reported upon by choosing any one of the following options):",
                  INTEGRITY_LABELS[reportingAssessment.integrity] || reportingAssessment.integrity || "—"
                )}
                {reportingAssessment.integrity === "doubtful" && reportingAssessment.secret_note_file && (() => {
                  const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/v1\/?$/, '');
                  const fileUrl = `${baseUrl}/secret-note/${reportingAssessment.kpi_report_id}/${reportingAssessment.secret_note_file}`;
                  const isImage = /\.(jpg|jpeg|png|gif)$/i.test(reportingAssessment.secret_note_file);
                  return (
                    <div className="mt-1">
                      <label className="font-weight-bold small text-muted d-block">Secret Note Attachment</label>
                      {isImage && (
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                          <img
                            src={fileUrl}
                            alt="Secret Note"
                            style={{ maxWidth: 120, maxHeight: 80, objectFit: 'cover', border: '1px solid #dee2e6', borderRadius: 4, display: 'block', marginBottom: 4 }}
                          />
                        </a>
                      )}
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="small">
                        <i className="fa fa-file mr-1" />
                        {reportingAssessment.secret_note_original_name || reportingAssessment.secret_note_file}
                      </a>
                    </div>
                  );
                })()}
              </div>
              <div className="col-md-6">
                {reportingAssessment.overall_grade && (
                  <div className="form-group mb-3">
                    <label className="font-weight-bold small text-muted">10. Overall grade (on a grade of 0–100) based on the grades awarded in Item no. 6 &amp; 7. This should be computed by summing up the weighted average grade indicated in Item no. 6 and Item no. 7.</label>
                    <div className="d-flex align-items-center mt-1">
                      <span className="fw-bold fs-4 mr-2">{parseFloat(reportingAssessment.overall_grade).toFixed(2)}</span>
                      <span className="text-muted">/ 100</span>
                    </div>
                    {reportingAssessment.kpi_total && (
                      <div className="small text-muted mt-1">
                        KPI Total: {parseFloat(reportingAssessment.kpi_total).toFixed(2)}
                        {reportingAssessment.attr_total && ` · Attributes Total: ${parseFloat(reportingAssessment.attr_total).toFixed(2)}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="col-md-12">
                {this.renderReadOnlyField("9. Pen picture by Reporting Officer. Please comment (in about 100 words) on the overall qualities of the officer including areas of strengths and those which need improvements. The pen picture should be consistent with the overall grade furnished in Item no. 10.", reportingAssessment.pen_picture || "", 4)}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  renderReviewingAssessmentSection = () => {
    const { kpiReport, reportingAssessment, repKpiGrades, repAttrGrades, reviewingAssessment, revKpiGrades, revAttrGrades, attrMasterList } = this.state;

    const repKpiMap: any = {};
    repKpiGrades.forEach((g: any) => { repKpiMap[g.kpi_task_id] = g; });
    const revKpiMap: any = {};
    revKpiGrades.forEach((g: any) => { revKpiMap[g.kpi_task_id] = g; });

    const repAttrMap: any = {};
    repAttrGrades.forEach((g: any) => { repAttrMap[g.attribute_master_id] = g; });
    const revAttrMap: any = {};
    revAttrGrades.forEach((g: any) => { revAttrMap[g.attribute_master_id] = g; });

    const mouTask = kpiReport?.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport?.tasks?.filter((t: any) => t.particular !== "*)") || [];

    return (
      <>
        <hr className="my-4" />
        {this.renderSectionHeader("apar-detail-reviewing", "fa-eye", "Reviewing Manager's Assessment", "Assessment submitted by the Reviewing Authority")}

        {!reviewingAssessment ? (
          <p className="text-muted fst-italic mb-4">Reviewing Manager has not submitted their assessment yet.</p>
        ) : (
          <>
            {/* KPI Grades Comparison */}
            <h6 className="fw-bold mb-3 text-secondary">A. KPI Grades — Reporting vs Reviewing</h6>
            <div className="table-responsive mb-4">
              <table className="table table-bordered table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Task</th>
                    <th className="text-center">Weightage</th>
                    <th className="text-center">RM Grade</th>
                    <th className="text-center">RVM Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {mouTask && (
                    <>
                      <tr className="table-secondary">
                        <td colSpan={5} className="fw-semibold small">I – MoU Target</td>
                      </tr>
                      <tr>
                        <td className="text-muted">*)</td>
                        <td>{mouTask.task_description || "—"}</td>
                        <td className="text-center">{mouTask.weightage || 0}</td>
                        <td className="text-center">{repKpiMap[mouTask.id] ? repKpiMap[mouTask.id].absolute_grade : "—"}</td>
                        <td className="text-center">{revKpiMap[mouTask.id] ? revKpiMap[mouTask.id].absolute_grade : "—"}</td>
                      </tr>
                    </>
                  )}
                  <tr className="table-secondary">
                    <td colSpan={5} className="fw-semibold small">II – Other Key Tasks</td>
                  </tr>
                  {otherTasks.map((task: any) => (
                    <tr key={task.id}>
                      <td className="text-muted">{task.particular}</td>
                      <td>{task.task_description || "—"}</td>
                      <td className="text-center">{task.weightage || 0}</td>
                      <td className="text-center">{repKpiMap[task.id] ? repKpiMap[task.id].absolute_grade : "—"}</td>
                      <td className="text-center">{revKpiMap[task.id] ? revKpiMap[task.id].absolute_grade : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Personal Attributes Comparison */}
            <h6 className="fw-bold mb-3 text-secondary">B. Personal Attributes — Reporting vs Reviewing</h6>
            {attrMasterList.length > 0 && (
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Attribute</th>
                      <th className="text-center">RM Grade</th>
                      <th className="text-center">RVM Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attrMasterList.map((attr: any, i: number) => (
                      <tr key={attr.id}>
                        <td>{i + 1}</td>
                        <td>{attr.attribute_name}</td>
                        <td className="text-center">{repAttrMap[attr.id] ? repAttrMap[attr.id].grade : "—"}</td>
                        <td className="text-center">{revAttrMap[attr.id] ? revAttrMap[attr.id].grade : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Review Conclusion */}
            <h6 className="fw-bold mb-3 text-secondary">C. Review Conclusion</h6>
            <div className="row mb-4">
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "Do you agree with the assessment made by the Reporting officer with respect to discharge of responsibilities and various attributes of the officer reported upon?",
                  reviewingAssessment.agree_with_discharge_assessment !== null && reviewingAssessment.agree_with_discharge_assessment !== undefined
                    ? (reviewingAssessment.agree_with_discharge_assessment ? "Yes" : "No")
                    : "—"
                )}
              </div>
              <div className="col-md-6">
                {this.renderReadOnlyField(
                  "Do you agree with the assessment of the Reporting officer in respect of extraordinary achievements and/or significant shortfalls of the officer reported upon?",
                  reviewingAssessment.agree_with_reporting !== null && reviewingAssessment.agree_with_reporting !== undefined
                    ? (reviewingAssessment.agree_with_reporting ? "Yes" : "No")
                    : "—"
                )}
              </div>
              <div className="col-md-12">
                {this.renderReadOnlyField("In case of difference of opinion, details and reasons for the same may be given.", reviewingAssessment.difference_details || "", 3)}
              </div>
              <div className="col-md-12">
                {this.renderReadOnlyField("Comments, if any, on the pen picture written by the Reporting Authority.", reviewingAssessment.pen_picture_comment || "", 4)}
              </div>
              {reviewingAssessment.overall_grade && (
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="font-weight-bold small text-muted">Overall Grade (Reviewing Manager)</label>
                    <div className="d-flex align-items-center mt-1">
                      <span className="fw-bold fs-4 mr-2">{parseFloat(reviewingAssessment.overall_grade).toFixed(2)}</span>
                      <span className="text-muted">/ 100</span>
                    </div>
                    {reviewingAssessment.kpi_total && (
                      <div className="small text-muted mt-1">
                        KPI Total: {parseFloat(reviewingAssessment.kpi_total).toFixed(2)}
                        {reviewingAssessment.attr_total && ` · Attributes Total: ${parseFloat(reviewingAssessment.attr_total).toFixed(2)}`}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { showLoader } = this.state;
    const { viewerRole } = this.props;

    if (showLoader) return <Loader />;

    return (
      <div className="mt-2">
        {/* Always shown: Employee's self-appraisal */}
        {this.renderKpiReportSection()}
        {this.renderSelfAppraisalSection()}

        {/* Shown for reviewing + accepting */}
        {(viewerRole === "reviewing" || viewerRole === "accepting") &&
          this.renderReportingAssessmentSection()}

        {/* Shown only for accepting */}
        {viewerRole === "accepting" && this.renderReviewingAssessmentSection()}
      </div>
    );
  }
}

export default AparFullDetailContent;
